import { PandaAvatar } from 'shared/images/characters';
import styles from './render-chat-messages.module.scss';
import React, { useMemo, useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { RenderQuestion } from './render-question';
import { RenderAnswer } from './render-answer';
import { Loader } from './chat-loader';
import { useSelector } from 'react-redux';
import { getUser } from 'shared/store/selectors/user.selector';
import { useChatConversation } from '../../hooks/use-chat-conversation';
import { faSquareChevronLeft, faSquareChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { formatFullName, formatNumber } from 'shared/utils';
import { IconButton } from 'src/components/design-system/buttons/icon-button';
import { AUTH_USER_TYPE_NORMAL } from 'shared/constants';
import { Avatar, UserAvatar } from 'src/components/avatar';

const C8_AI = 'C8 Panda';

function toHistoryArray(message) {
  const result = [message];
  message.question.history?.forEach((question, index) => {
    result.push({
      question,
      answer: message.answer.history[index],
    });
  });
  return result.reverse();
}

function RenderQuestionAnswer({ message, renderAvatar, index }) {
  const { lang, isMessageLoading, messages } = useChatConversation();
  const historyArray = useMemo(() => toHistoryArray(message), [message]);
  const [historyIndex, setHistoryIndex] = useState(historyArray.length - 1); // start with the last element

  useEffect(() => {
    setHistoryIndex(historyArray.length - 1);
  }, [historyArray]);

  const toggle = leftOrRight => {
    setHistoryIndex(prev => prev + leftOrRight);
  };

  const renderHistoryToggle = useCallback(() => {
    if (historyArray.length <= 1) return null;

    return (
      <div className={styles.history}>
        <IconButton disabled={historyIndex === 0} onClick={() => toggle(-1)} label="Previous">
          <FontAwesomeIcon icon={faSquareChevronLeft} />
        </IconButton>
        <p>{`${formatNumber(historyIndex + 1)}/${formatNumber(historyArray.length)}`}</p>
        <IconButton
          disabled={historyIndex === historyArray.length - 1}
          onClick={() => toggle(1)}
          label="Next">
          <FontAwesomeIcon icon={faSquareChevronRight} />
        </IconButton>
      </div>
    );
  }, [historyArray.length, historyIndex]);

  /**
   * @type {React.MutableRefObject<HTMLDivElement | null>}
   */
  const questionRef = useRef(null);

  const isLast = index === messages.length - 1;

  useLayoutEffect(() => {
    if (isLast && questionRef.current) {
      const scrollContainer = questionRef.current.closest(`.${styles.root}`);
      if (scrollContainer) {
        const questionTop = questionRef.current.getBoundingClientRect().top;
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const offset = questionTop - containerTop + scrollContainer.scrollTop;

        scrollContainer.scrollTo({
          top: offset - 16, // small padding from top
          behavior: 'smooth',
        });
      }
    }
  }, [isLast, message]);

  return (
    <div ref={questionRef}>
      <RenderQuestion
        message={historyArray[historyIndex].question}
        renderAvatar={renderAvatar}
        renderHistoryToggle={renderHistoryToggle}
        mainQuestionId={message.question.id}
      />

      {isMessageLoading(index) ? (
        <div className={styles.message}>
          {renderAvatar(false)}

          <div className={styles.content}>
            <h4>{lang.BOT_NAME}</h4>
            <Loader />
            <div className={styles.spacer}></div>
          </div>
        </div>
      ) : (
        <RenderAnswer message={historyArray[historyIndex].answer} renderAvatar={renderAvatar} />
      )}
    </div>
  );
}

export function RenderChatMessages() {
  const userProfile = useSelector(getUser);
  const { lang, clearChat, messages } = useChatConversation();

  const renderAvatar = useCallback(
    isUser => {
      if (isUser) {
        return (
          <div className={styles.avatarWrapper}>
            <UserAvatar user={userProfile} />
          </div>
        );
      }

      return (
        <div className={styles.avatarWrapper}>
          <Avatar src={PandaAvatar} alt="" />
        </div>
      );
    },
    [userProfile],
  );

  const renderWelcomeMessage = useCallback(() => {
    if (messages.length > 0) return null;
    return (
      <div className={styles.message}>
        {renderAvatar(false)}
        <div className={styles.content}>
          <h4>{C8_AI}</h4>
          <p>
            {lang.WELCOME_MESSAGE.replace(
              '{fullName}',
              userProfile.type === AUTH_USER_TYPE_NORMAL
                ? formatFullName(userProfile)
                : lang.HI_THERE,
            )}
          </p>
        </div>
      </div>
    );
  }, [lang.WELCOME_MESSAGE, renderAvatar, userProfile, lang.HI_THERE, messages.length]);

  return (
    <div className={classNames(styles.root, 'comprehensive-text')}>
      {messages.length > 0 && (
        <button type="button" className={styles.clearChat} onClick={clearChat}>
          {lang.CLEAR_CHAT}
        </button>
      )}
      {renderWelcomeMessage()}

      {messages.map((message, index) => {
        return (
          <RenderQuestionAnswer
            key={message.id}
            message={message}
            renderAvatar={renderAvatar}
            index={index}
          />
        );
      })}
    </div>
  );
}
